import { pick, uniq, isPlainObject } from 'lodash'
import { captureSentryException } from '@edulastic/common'
import { questionType } from '@edulastic/constants'
import { alignmentStandardsFromUIToMongo } from '../../../assessment/utils/helpers'
import gradesMap from '../../Reports/common/static/json/gradesMap.json'

const {
  MULTIPLE_CHOICE,
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_DRAG_DROP,
  CLOZE_DROP_DOWN,
  CLOZE_TEXT,
  CLOZE_IMAGE_DRAG_DROP,
  CLOZE_IMAGE_DROP_DOWN,
  CLOZE_IMAGE_TEXT,
  EXPRESSION_MULTIPART,
  EDITING_TASK,
  CHOICE_MATRIX,
} = questionType

/**
 * Question types that only require stimulus (no options) to generate metadata
 */
const STIMULUS_ONLY_QTYPES = [
  ESSAY_RICH_TEXT,
  ESSAY_PLAIN_TEXT,
  CLOZE_TEXT,
  CLOZE_IMAGE_TEXT,
]

/**
 * Builds tooltip items for a standard using curriculum details
 * @param {Object} standard - Standard object with curriculumId and grades
 * @param {Array} allCurriculums - List of all curriculums
 * @returns {Array<{ label: string, value: string }>} - Tooltip items
 */
export const getStandardTooltipItems = (standard, allCurriculums = []) => {
  if (!standard?.curriculumId || !allCurriculums.length) {
    return []
  }

  const curriculum = allCurriculums.find((c) => c._id === standard.curriculumId)
  const curriculumName = curriculum?.curriculum || ''
  const subject = curriculum?.subject || ''
  const grades = (standard.grades || [])
    .slice()
    .sort(
      (a, b) =>
        (gradesMap[a]?.sortKey ?? Infinity) -
        (gradesMap[b]?.sortKey ?? Infinity)
    )

  return [
    curriculumName && { label: 'Standard Set', value: curriculumName },
    subject && { label: 'Subject', value: subject },
    grades.length > 0 && { label: 'Grade', value: grades.join(', ') },
  ].filter(Boolean)
}

/**
 * Extracts interested curriculum IDs from the curriculums list
 * @param {Array} allInterestedCurriculums - List of interested all curriculums
 * @returns {string[]} - Array of curriculum IDs
 */
export const getInterestedCurriculumIds = (allInterestedCurriculums = []) =>
  (allInterestedCurriculums || []).map((curr) => curr._id)

const standardFields = [
  '_id',
  'level',
  'grades',
  'identifier',
  'tloIdentifier',
  'tloId',
  'tloDescription',
  'eloId',
  'subEloId',
  'description',
  'curriculumId',
  'pdlId',
]

/**
 * Helper to extract options from object format { responseId: ['Option A', 'Option B'] }
 * @param {Object} optionsObj - Options object
 * @returns {string[]} - Flattened array of option strings
 */
const extractOptionsFromObject = (optionsObj) => {
  if (!isPlainObject(optionsObj)) {
    return []
  }

  return Object.values(optionsObj)
    .flat()
    .filter((opt) => typeof opt === 'string' && opt)
}

/**
 * Builds the payload for AI metadata generation API based on question type
 * @param {Object} question - Question data object
 * @returns {Object} - { stimulus: string, options: string[] }
 */
export const buildQuestionMetadataPayload = (question) => {
  if (!question) {
    return { stimulus: '', options: [] }
  }

  const { type, stimulus = '', grades = [], subjects = [] } = question
  let options = []

  switch (type) {
    case MULTIPLE_CHOICE:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CHOICE_MATRIX:
      options = (question.stems || []).filter(Boolean)
      break

    case CLOZE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.label)
        .filter(Boolean)
      break

    case CLOZE_DROP_DOWN:
      options = extractOptionsFromObject(question.options)
      break

    case CLOZE_IMAGE_DRAG_DROP:
      options = (question.options || [])
        .map((opt) => opt?.value)
        .filter(Boolean)
      break

    case CLOZE_IMAGE_DROP_DOWN:
      options = (question.options || []).flat().filter(Boolean)
      break

    case EXPRESSION_MULTIPART:
      options = extractOptionsFromObject(question.options)
      break

    case EDITING_TASK:
      options = extractOptionsFromObject(question.options)
      break

    case ESSAY_RICH_TEXT:
    case ESSAY_PLAIN_TEXT:
    case CLOZE_TEXT:
    case CLOZE_IMAGE_TEXT:
      break

    default:
      break
  }

  return {
    stimulus,
    qType: type,
    ...(options?.length > 0 ? { options } : {}),
    ...(grades?.length > 0 ? { grades } : {}),
    ...(subjects?.length > 0 ? { subject: subjects[0] } : {}),
  }
}

/**
 * Checks if a question has sufficient content for AI metadata generation
 * based on the question type
 * @param {Object} question - Question data object
 * @returns {boolean} - Whether the question has enough content
 */
export const hasQuestionContentForMetadata = (question) => {
  if (!question) return false

  const { type, stimulus } = question
  const hasStimulusContent =
    typeof stimulus === 'string' && stimulus.trim().length > 0

  if (STIMULUS_ONLY_QTYPES.includes(type)) {
    return hasStimulusContent
  }

  const formattedQuestion = buildQuestionMetadataPayload(question)
  const { options } = formattedQuestion

  const hasOptionsContent = Array.isArray(options) && options.length > 0

  return hasStimulusContent && hasOptionsContent
}

/**
 * Applies AI suggested metadata to question data
 * @param {Object} params - Parameters object
 * @param {Object} params.question - Current question data
 * @param {Object} params.suggestedMetaData - AI suggested metadata from state
 * @param {Array} params.allCurriculums - List of all curriculums from state
 * @param {Array} params.dictAlignments - Dictionaries alignments with standards in UI format
 * @returns {Object} - Updated question with suggested metadata applied
 */
export const applyAIMetadataToQuestion = ({
  question,
  suggestedMetaData = {},
  allCurriculums,
  dictAlignments,
}) => {
  if (!suggestedMetaData) return question

  const { standards: suggestedStandards = [], dok, difficulty } =
    suggestedMetaData || {}

  if (!suggestedStandards || suggestedStandards?.length === 0) {
    return {
      ...question,
      ...(dok && { depthOfKnowledge: dok }),
      ...(difficulty && { authorDifficulty: difficulty }),
    }
  }

  const standard = suggestedStandards[0]
  const newStandard = pick(standard, standardFields)
  const curriculumId = standard.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)
  const uniqSubjects = [curriculum.subject].filter((item) => !!item)
  let questionGrades = (newStandard.grades || []).filter((item) => !!item)

  if (!curriculum) {
    captureSentryException(
      new Error('Curriculum not found for suggested standard'),
      {
        errorMessage:
          'AI Question Assistance - METADATA: Curriculum not found for suggested standard',
        suggestedStandard: standard,
      }
    )
    return question
  }

  const existingAlignments = (question.alignment || []).filter(
    (a) => !a.isEquivalentStandard
  )

  const existingAlignmentIndex = existingAlignments.findIndex(
    (a) => a.curriculumId === curriculumId
  )

  let updatedAlignments

  if (existingAlignmentIndex !== -1) {
    const dictAlignment = dictAlignments.find(
      (a) => a.curriculumId === curriculumId
    )
    const existingStandards = dictAlignment?.standards || []

    const standardExists = existingStandards.some(
      (s) => s._id === newStandard._id
    )

    const allStandards = standardExists
      ? existingStandards
      : [...existingStandards, newStandard]

    const standardsGrades = allStandards.flatMap(
      (_standard) => _standard.grades
    )

    questionGrades = uniq([...standardsGrades]).filter((item) => !!item)

    const updatedDomains = alignmentStandardsFromUIToMongo(allStandards)

    updatedAlignments = existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, grades: [], domains: updatedDomains }
        : alignment
    )
  } else {
    const newQuestionAlignment = {
      curriculum: curriculum.curriculum,
      curriculumId,
      subject: curriculum.subject,
      grades: [],
      domains: alignmentStandardsFromUIToMongo([newStandard]),
    }
    updatedAlignments = [...existingAlignments, newQuestionAlignment]
  }

  return {
    ...question,
    alignment: updatedAlignments,
    grades: questionGrades,
    subjects: uniqSubjects,
    ...(dok && { depthOfKnowledge: dok }),
    ...(difficulty && { authorDifficulty: difficulty }),
  }
}

/**
 * Builds updated dictionaries alignments for UI sync
 * @param {Object} suggestedMetaData - AI suggested metadata from state
 * @param {Array} allCurriculums - List of all curriculums from state
 * @param {Array} existingAlignments - Current alignments from dictionaries state
 * @returns {Array} - Updated alignments array
 */
export const buildDictAlignmentFromAIMetadata = (
  suggestedMetaData,
  allCurriculums,
  existingAlignments
) => {
  if (!suggestedMetaData?.standards?.length > 0) return existingAlignments

  const { standards: suggestedStandards = [] } = suggestedMetaData || {}

  const standard = suggestedStandards[0]
  const newStandard = pick(standard, standardFields)
  const curriculumId = standard.curriculumId
  const curriculum = allCurriculums.find((c) => c._id === curriculumId)

  if (!curriculum) {
    return existingAlignments
  }

  const existingAlignmentIndex = existingAlignments.findIndex(
    (a) => a.curriculumId === curriculumId
  )

  if (existingAlignmentIndex !== -1) {
    const existingAlignment = existingAlignments[existingAlignmentIndex]
    const existingStandards = existingAlignment.standards || []

    const standardExists = existingStandards.some(
      (s) => s._id === newStandard._id
    )

    if (standardExists) return existingAlignments

    const updatedStandards = [...existingStandards, newStandard]

    return existingAlignments.map((alignment, index) =>
      index === existingAlignmentIndex
        ? { ...alignment, standards: updatedStandards }
        : alignment
    )
  }

  const newDictAlignment = {
    curriculum: curriculum.curriculum,
    curriculumId,
    subject: curriculum.subject,
    grades: [],
    standards: [newStandard],
  }
  return [...existingAlignments, newDictAlignment]
}
